import {useQuery} from '@tanstack/react-query';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';
import {privacyMonitorUserStatusCode} from '../constants/errorCodeMap';

/**
 * Checks if PMA is disabled for the user.
 * @param {string} whiteLabelClient  - Optional: white label client.
 * @returns {boolean} true if PMA is disabled, false otherwise.
 */
const isPMADisabled = (whiteLabelClient = '') => {
  const platformType = utils.getPlatformType();
  const isIOs = platformType === 'iOS';
  return isIOs && whiteLabelClient === 'moneylion';
};
/**
 *  Fetch user privacy monitor summary results through member-api.
 * @param {string} countryCode  - user's country code.
 * @param {string} whiteLabelClient  - Optional: white label client.
 * @return {Object} privacy monitor summary data, various response status(s),
 * and customize object for handling dashboard privacy monitor widget logics.
 */
export const usePrivacyMonitorSummaryResults = (countryCode: string, whiteLabelClient = '') => {
  const url = utils.getLPMPrivacyResultsSummaryPath();
  const isDisabled = isPMADisabled(whiteLabelClient);
  const enabled = (countryCode === 'US' || countryCode === 'CA') && !isDisabled;

  const {isError, error, isPending, isSuccess, data, refetch} = useQuery({
    queryKey: ['privacyMonitor', countryCode],
    queryFn: () =>
      fetchRest(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      }),
    enabled,
  });

  if (!enabled) {
    return {
      data: null,
      refetch: () => {},
      isLoading: false,
      isSuccess: false,
      isError: true,
      error: new Error('Privacy monitor features disabled.'),
    };
  }

  const topThreeResults = (data as any)?.topThreeResults;
  let topTwoResults: any[] = [];
  if (topThreeResults && topThreeResults.length > 0) {
    topTwoResults = topThreeResults.length === 3 ? topThreeResults.slice(0, -1) : [...topThreeResults];
  }

  const privacyMonitor = {
    ...(data as any),
    topTwoResults,
    isNewUser: (error as unknown as PrivacyError)?.errorCode === privacyMonitorUserStatusCode || false,
  };

  return {isError, error, isLoading: isPending, isSuccess, data: privacyMonitor, refetch};
};

/**
 *  Fetch user license information for Privacy Monitor from colp through member-api.
 *  License info will be used to determine entitlement status of the user to provide member experience on Dashboard.
 * @param {string} countryCode  - user's country code.
 * @param {string} whiteLabelClient  - Optional: white label client.
 * @return {Object} license data, and various response status(s)
 */
export const usePrivacyMonitorLicense = (countryCode: string, whiteLabelClient = '') => {
  const url = utils.getValidateLicensePath();
  const isDisabled = isPMADisabled(whiteLabelClient);
  const enabled = (countryCode === 'US' || countryCode === 'CA') && !isDisabled;

  const query = useQuery({
    queryKey: ['privacyMonitorLicense', countryCode],
    queryFn: () => fetchRest(url),
    enabled,
  });

  if (!enabled) {
    return {
      data: {validateLicense: false, validLicense: false},
      refetch: () => {},
      isLoading: false,
      isError: false,
      isSuccess: false,
      error: null,
    };
  }

  return {...query, isLoading: query.isPending};
};
